import { InputField } from '@advanza/input'
import Card from 'components/Card'
import Restricted from 'components/Restricted'
import Page from 'components/ui/Page'
import React, { useMemo, useState } from 'react'
import {
    AppStoreBadgeShowcase,
    AvatarGroupShowcase,
    AvatarLabelGroupShowcase,
    AvatarProfilePhotoShowcase,
    AvatarShowcase,
    BadgeShowcase,
    ButtonGroupShowcase,
    ButtonShowcase,
    CheckboxShowcase,
    DropdownShowcase,
    InputShowcase,
    ProgressBarShowcase,
    ProgressCircleShowcase,
    RadioGroupShowcase,
    RadioShowcase,
    RangeSliderShowcase,
    SelectShowcase,
    SocialButtonGroupShowcase,
    SocialButtonShowcase,
    SwitchShowcase,
    TagShowcase,
    TextareaShowcase,
    TextEditorShowcase,
    TooltipShowcase,
} from './components'
import { Copyable, InlineCode, Section } from './components/shared'
import { designTokens, FontSizeValue } from './designTokens'
import { ComponentSubjectContext } from './verification/componentSubjectSlot'
import { componentSubject, foundationSubject } from './verification/subjects'
import { VerificationBadge } from './verification/VerificationBadge'
import { VerificationProvider } from './verification/VerificationContext'

const REPO_BASE = 'https://github.com/trustlocal/trustoo/blob/TRUS-8105'
const FIGMA_FOUNDATIONS_URL = 'https://www.figma.com/design/Ychy17er3OhvZzmSnHRCgp'

type DesignSystemTab = 'foundations' | 'components'

// Defensive safety-net for category keys that include slashes. Every known
// slash-keyed Figma path is promoted to a flat namespace in the token
// transformer (`tools/token_transformer/src/names.ts`), so this should
// effectively never fire today. We keep the check + the conditional warning
// panel below so a future Figma export that introduces a new multi-segment
// path surfaces loudly instead of silently producing arbitrary-value classes.
function isSlashCategory(category: string): boolean {
    return category.includes('/')
}

// Tailwind reads the literal `DEFAULT` shade as the no-suffix variant, e.g.
// `colors['focus-ring'].DEFAULT` renders as `outline-focus-ring`, not
// `outline-focus-ring-DEFAULT`. Mirror that here so the suggested class
// matches the actual emitted utility.
function buildColorClass(prefix: 'bg' | 'text' | 'border', category: string, key: string): string {
    if (isSlashCategory(category)) {
        return `${prefix}-[${category}/${key}]`
    }
    if (key === 'DEFAULT') {
        return `${prefix}-${category}`
    }
    return `${prefix}-${category}-${key}`
}

interface ColorSwatchProps {
    category: string
    name: string
    value: string
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({ category, name, value }) => {
    const bgClass = buildColorClass('bg', category, name)
    return (
        <div className="flex items-start gap-spacing-lg rounded-radius-xs border border-border-secondary bg-base-white p-spacing-md">
            <div
                aria-hidden
                className="h-10 w-10 shrink-0 rounded-radius-xs border border-border-primary"
                style={{ backgroundColor: value }}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-spacing-xxs">
                <div className="truncate text-sm font-medium text-text-primary">{name}</div>
                <Copyable label={value} value={value} />
                <Copyable label={bgClass} value={bgClass} icon />
            </div>
            <VerificationBadge subject={foundationSubject(['colors', category, name], value)} />
        </div>
    )
}

interface SpacingRowProps {
    name: string
    value: string
}

const SpacingRow: React.FC<SpacingRowProps> = ({ name, value }) => {
    const pixelValue = parseFloat(value)
    return (
        <div className="flex items-start gap-spacing-lg rounded-radius-xs border border-border-secondary bg-base-white p-spacing-md">
            <div className="flex h-6 w-32 shrink-0 items-center">
                <div
                    aria-hidden
                    className="h-2 rounded-radius-xs bg-background-bg-brand-solid"
                    style={{ width: Math.min(pixelValue, 128) }}
                />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-spacing-xxs">
                <div className="truncate text-sm font-medium text-text-primary">{name}</div>
                <Copyable label={value} value={value} />
                <Copyable label={`p-${name}`} value={`p-${name}`} icon />
            </div>
            <VerificationBadge subject={foundationSubject(['spacing', name], value)} />
        </div>
    )
}

interface RadiusRowProps {
    name: string
    value: string
}

const RadiusRow: React.FC<RadiusRowProps> = ({ name, value }) => (
    <div className="flex items-start gap-spacing-lg rounded-radius-xs border border-border-secondary bg-base-white p-spacing-md">
        <div
            aria-hidden
            className="h-10 w-10 shrink-0 border border-border-primary bg-background-bg-brand-primary"
            style={{ borderRadius: value }}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-spacing-xxs">
            <div className="truncate text-sm font-medium text-text-primary">{name}</div>
            <Copyable label={value} value={value} />
            <Copyable label={`rounded-${name}`} value={`rounded-${name}`} icon />
        </div>
        <VerificationBadge subject={foundationSubject(['borderRadius', name], value)} />
    </div>
)

interface FontSizeRowProps {
    name: string
    value: FontSizeValue
}

const FontSizeRow: React.FC<FontSizeRowProps> = ({ name, value }) => {
    const size = Array.isArray(value) ? value[0] : value
    const lineHeight = Array.isArray(value) ? value[1]?.lineHeight : undefined
    return (
        <div className="flex items-start gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-lg">
            <div className="flex w-48 shrink-0 flex-col gap-spacing-xxs">
                <div className="text-sm font-medium text-text-primary">{name}</div>
                <span className="font-mono text-xs text-text-tertiary">
                    {size}
                    {lineHeight && ` / ${lineHeight}`}
                </span>
                <Copyable label={`text-${name}`} value={`text-${name}`} icon />
            </div>
            <div className="min-w-0 flex-1">
                <span
                    style={{
                        fontSize: size,
                        lineHeight: lineHeight || undefined,
                        fontFamily: 'Aspekta, sans-serif',
                    }}>
                    The quick brown fox jumps over the lazy dog
                </span>
            </div>
            <VerificationBadge subject={foundationSubject(['fontSize', name], value)} />
        </div>
    )
}

interface FontWeightRowProps {
    name: string
    value: string
}

const FontWeightRow: React.FC<FontWeightRowProps> = ({ name, value }) => (
    <div className="flex items-start gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-md">
        <div className="flex w-40 shrink-0 flex-col gap-spacing-xxs">
            <div className="text-sm font-medium text-text-primary">{name}</div>
            <Copyable label={value} value={value} />
            <Copyable label={`font-${name}`} value={`font-${name}`} icon />
        </div>
        <div className="min-w-0 flex-1 text-lg text-text-primary" style={{ fontWeight: value }}>
            Aa Trustoo
        </div>
        <VerificationBadge subject={foundationSubject(['fontWeight', name], value)} />
    </div>
)

interface FontFamilyRowProps {
    name: string
    stack: string[]
}

const FontFamilyRow: React.FC<FontFamilyRowProps> = ({ name, stack }) => {
    // Sentient (the italics face) ships italic-only, so the preview must be
    // italic to actually render it.
    const isItalicFace = stack.includes('Sentient')
    return (
        <div className="flex items-start gap-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-md">
            <div className="flex w-40 shrink-0 flex-col gap-spacing-xxs">
                <div className="text-sm font-medium text-text-primary">{name}</div>
                <Copyable label={stack.join(', ')} value={stack.join(', ')} />
                <Copyable label={`font-${name}`} value={`font-${name}`} icon />
            </div>
            <div
                className="min-w-0 flex-1 text-display-sm text-text-primary"
                style={{
                    fontFamily: [...stack, 'sans-serif'].join(', '),
                    fontStyle: isItalicFace ? 'italic' : undefined,
                }}>
                Aa Trustoo
            </div>
            <VerificationBadge subject={foundationSubject(['fontFamily', name], stack)} />
        </div>
    )
}

interface TokenValueRowProps {
    group: string
    name: string
    value: string
    classPrefix: string
}

// The value-only token shapes (line height, max width): no preview, just the
// value and the utility class.
const TokenValueRow: React.FC<TokenValueRowProps> = ({ group, name, value, classPrefix }) => (
    <div className="flex items-start justify-between gap-spacing-lg rounded-radius-xs border border-border-secondary bg-base-white p-spacing-md">
        <div className="flex min-w-0 flex-col gap-spacing-xxs">
            <div className="truncate text-sm font-medium text-text-primary">{name}</div>
            <Copyable label={value} value={value} />
            <Copyable label={`${classPrefix}-${name}`} value={`${classPrefix}-${name}`} icon />
        </div>
        <VerificationBadge subject={foundationSubject([group, name], value)} />
    </div>
)

// The Components page: one entry per showcase. `version` is the version the
// showcase imports, and is what the sign-off is recorded against — bump it in
// the same edit that points a showcase at a new version.
const COMPONENT_SHOWCASES: { name: string; version: string; Showcase: React.FC }[] = [
    { name: 'Button', version: 'v2', Showcase: ButtonShowcase },
    { name: 'ButtonGroup', version: 'v2', Showcase: ButtonGroupShowcase },
    { name: 'SocialButton', version: 'v2', Showcase: SocialButtonShowcase },
    { name: 'SocialButtonGroup', version: 'v2', Showcase: SocialButtonGroupShowcase },
    { name: 'AppStoreBadge', version: 'v2', Showcase: AppStoreBadgeShowcase },
    { name: 'Badge', version: 'v2', Showcase: BadgeShowcase },
    { name: 'Tag', version: 'v2', Showcase: TagShowcase },
    { name: 'Switch', version: 'v2', Showcase: SwitchShowcase },
    { name: 'Checkbox', version: 'v2', Showcase: CheckboxShowcase },
    { name: 'Radio', version: 'v2', Showcase: RadioShowcase },
    { name: 'RadioGroup', version: 'v2', Showcase: RadioGroupShowcase },
    { name: 'RangeSlider', version: 'v2', Showcase: RangeSliderShowcase },
    { name: 'ProgressBar', version: 'v2', Showcase: ProgressBarShowcase },
    { name: 'ProgressCircle', version: 'v2', Showcase: ProgressCircleShowcase },
    { name: 'Avatar', version: 'v2', Showcase: AvatarShowcase },
    { name: 'AvatarGroup', version: 'v2', Showcase: AvatarGroupShowcase },
    { name: 'AvatarLabelGroup', version: 'v2', Showcase: AvatarLabelGroupShowcase },
    { name: 'AvatarProfilePhoto', version: 'v2', Showcase: AvatarProfilePhotoShowcase },
    { name: 'Tooltip', version: 'v2', Showcase: TooltipShowcase },
    { name: 'Input', version: 'v2', Showcase: InputShowcase },
    { name: 'Textarea', version: 'v2', Showcase: TextareaShowcase },
    { name: 'Select', version: 'v2', Showcase: SelectShowcase },
    { name: 'Dropdown', version: 'v2', Showcase: DropdownShowcase },
    { name: 'TextEditor', version: 'v2', Showcase: TextEditorShowcase },
]

// Surfaced in the "How to use" card only if the live tokens snapshot actually
// contains a slash-keyed category. See the comment on `isSlashCategory`.
const slashCategoriesInUse = Object.keys(designTokens.colors).filter(isSlashCategory)

const DesignSystemPage: React.FC<{ section?: DesignSystemTab }> = ({ section = 'foundations' }) => {
    const [filter, setFilter] = useState('')
    const normalizedFilter = filter.trim().toLowerCase()

    const filteredColors = useMemo(() => {
        const entries: { category: string; name: string; value: string }[] = []
        for (const [category, group] of Object.entries(designTokens.colors)) {
            for (const [name, value] of Object.entries(group)) {
                if (
                    !normalizedFilter ||
                    category.toLowerCase().includes(normalizedFilter) ||
                    name.toLowerCase().includes(normalizedFilter) ||
                    value.toLowerCase().includes(normalizedFilter)
                ) {
                    entries.push({ category, name, value })
                }
            }
        }
        return entries
    }, [normalizedFilter])

    const colorsByCategory = useMemo(() => {
        const map = new Map<string, { name: string; value: string }[]>()
        for (const entry of filteredColors) {
            const bucket = map.get(entry.category) ?? []
            bucket.push({ name: entry.name, value: entry.value })
            map.set(entry.category, bucket)
        }
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
    }, [filteredColors])

    return (
        <Page
            title={
                section === 'components'
                    ? 'Design System · Components'
                    : 'Design System · Foundations'
            }>
            <Restricted location="design_system" checkWrite={false} showMessage>
                <VerificationProvider>
                    <div className="m-auto my-spacing-7xl max-w-container-max-width-desktop space-y-spacing-4xl px-spacing-xl">
                        <header className="space-y-spacing-lg">
                            <h1 className="text-display-md font-bold text-text-primary">
                                {section === 'components' ? 'Components' : 'Foundations'}
                            </h1>
                            {section === 'foundations' ? (
                                <p className="max-w-width-xl text-lg text-text-secondary">
                                    Every token exported from the{' '}
                                    <a
                                        href={FIGMA_FOUNDATIONS_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-text-brand-tertiary underline">
                                        ❖ Foundations
                                    </a>{' '}
                                    file by the Trustoo Figma Export plugin (transformed into{' '}
                                    <InlineCode>design-tokens/tokens.cjs</InlineCode> and{' '}
                                    <InlineCode>deepMerge</InlineCode>'d into{' '}
                                    <InlineCode>tailwind.config.js</InlineCode>). Click any value or
                                    class to copy it.
                                </p>
                            ) : (
                                <p className="max-w-width-xl text-lg text-text-secondary">
                                    Live previews of the typed React components built on top of the{' '}
                                    <span className="font-semibold">Foundations</span> tokens. Each
                                    section shows the variants, sizes and states with copy-paste
                                    usage snippets.
                                </p>
                            )}
                        </header>

                        {section === 'components' &&
                            COMPONENT_SHOWCASES.map(({ name, version, Showcase }) => (
                                <ComponentSubjectContext.Provider
                                    key={name}
                                    value={componentSubject(name, version)}>
                                    <Showcase />
                                </ComponentSubjectContext.Provider>
                            ))}

                        {section === 'foundations' && (
                            <>
                                <Card header="How to use">
                                    <div className="space-y-spacing-xl text-sm text-text-primary">
                                        <div>
                                            <span className="font-semibold">1. Find a token.</span>{' '}
                                            Filter colors with the search box, or scroll through the
                                            spacing / radius / typography sections. The class name
                                            shown next to each value is the one to drop into{' '}
                                            <InlineCode>className</InlineCode>.
                                        </div>
                                        <div>
                                            <span className="font-semibold">
                                                2. Compose with Tailwind utilities.
                                            </span>{' '}
                                            Nested categories join with hyphens, so{' '}
                                            <InlineCode>{`colors.background['bg-brand-solid']`}</InlineCode>{' '}
                                            becomes{' '}
                                            <InlineCode>bg-background-bg-brand-solid</InlineCode>.
                                            Flat scales like <InlineCode>brand-500</InlineCode> stay
                                            short: <InlineCode>bg-brand-500</InlineCode>,{' '}
                                            <InlineCode>text-brand-500</InlineCode>,{' '}
                                            <InlineCode>border-brand-500</InlineCode>.
                                        </div>
                                        <div>
                                            <span className="font-semibold">
                                                3. Don't hardcode hex.
                                            </span>{' '}
                                            If a class doesn't resolve, the Foundations file
                                            probably hasn't shipped that variable yet, so run the
                                            plugin's Tokens tab against the Foundations file, or
                                            fall back to a sibling token. See{' '}
                                            <a
                                                className="text-text-brand-tertiary underline"
                                                href={`${REPO_BASE}/docs/figma_token_transformer.md`}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                docs/figma_token_transformer.md
                                            </a>{' '}
                                            for the pipeline details.
                                        </div>
                                        <div>
                                            <span className="font-semibold">4. Pipeline:</span>{' '}
                                            <InlineCode>Foundations.fig</InlineCode> →{' '}
                                            <InlineCode>
                                                plugin → design_tokens/manifest.json
                                            </InlineCode>{' '}
                                            → <InlineCode>tools/token_transformer</InlineCode> →{' '}
                                            <InlineCode>design-tokens/tokens.cjs</InlineCode> →{' '}
                                            <InlineCode>tailwind.config.js</InlineCode>.
                                        </div>
                                        {slashCategoriesInUse.length > 0 && (
                                            <div className="rounded-radius-xs border border-amber-200 bg-amber-50 p-spacing-lg text-amber-800">
                                                <span className="font-semibold">
                                                    Heads up, unrewritten slash keys:
                                                </span>{' '}
                                                {slashCategoriesInUse.length} color categor
                                                {slashCategoriesInUse.length === 1 ? 'y' : 'ies'} (
                                                {slashCategoriesInUse.map((c) => (
                                                    <InlineCode key={c}>{c}</InlineCode>
                                                ))}
                                                ) still contain slashes. Tailwind can't generate
                                                valid utilities for those keys, so the page falls
                                                back to arbitrary-value classes (
                                                <InlineCode>bg-[…]</InlineCode>). Add a rewrite rule
                                                in{' '}
                                                <InlineCode>
                                                    tools/token_transformer/src/names.ts
                                                </InlineCode>{' '}
                                                to promote them to a flat namespace.
                                            </div>
                                        )}
                                    </div>
                                </Card>

                                <Section
                                    title={`Colors (${filteredColors.length})`}
                                    description={
                                        <>
                                            Sourced from{' '}
                                            <InlineCode>{`tokens.colors[<category>][<name>]`}</InlineCode>
                                            . The background variant is shown; the same key works
                                            with <InlineCode>text-</InlineCode> and{' '}
                                            <InlineCode>border-</InlineCode> prefixes.
                                        </>
                                    }>
                                    <div className="mb-spacing-xl">
                                        <InputField
                                            placeholder="Filter by category, name, or hex…"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setFilter(e.target.value)
                                            }
                                            value={filter}
                                        />
                                    </div>
                                    {colorsByCategory.length === 0 && (
                                        <div className="text-sm text-text-tertiary">
                                            No colors match "{filter}".
                                        </div>
                                    )}
                                    <div className="space-y-spacing-3xl">
                                        {colorsByCategory.map(([category, items]) => (
                                            <div key={category}>
                                                <div className="mb-spacing-md flex items-center gap-spacing-md">
                                                    <h3 className="font-mono text-sm font-semibold text-text-primary">
                                                        {category}
                                                    </h3>
                                                    <span className="text-xs text-text-tertiary">
                                                        {items.length} token
                                                        {items.length === 1 ? '' : 's'}
                                                    </span>
                                                    {isSlashCategory(category) && (
                                                        <span className="rounded-radius-xs bg-amber-100 px-spacing-sm py-spacing-xxs text-xs text-amber-800">
                                                            slash in key, use arbitrary-value class
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 gap-spacing-md sm:grid-cols-2 lg:grid-cols-3">
                                                    {items.map((item) => (
                                                        <ColorSwatch
                                                            key={`${category}/${item.name}`}
                                                            category={category}
                                                            name={item.name}
                                                            value={item.value}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Section>

                                <Section
                                    title={`Spacing (${Object.keys(designTokens.spacing).length})`}
                                    description={
                                        <>
                                            Use with any spacing utility:{' '}
                                            <InlineCode>p-*</InlineCode>,{' '}
                                            <InlineCode>m-*</InlineCode>,{' '}
                                            <InlineCode>gap-*</InlineCode>,{' '}
                                            <InlineCode>w-*</InlineCode>,{' '}
                                            <InlineCode>h-*</InlineCode>, etc.
                                        </>
                                    }>
                                    <div className="grid grid-cols-1 gap-spacing-md md:grid-cols-2 lg:grid-cols-3">
                                        {Object.entries(designTokens.spacing).map(
                                            ([name, value]) => (
                                                <SpacingRow key={name} name={name} value={value} />
                                            ),
                                        )}
                                    </div>
                                </Section>

                                <Section
                                    title={`Border radius (${Object.keys(designTokens.borderRadius).length})`}
                                    description={
                                        <>
                                            Use with <InlineCode>rounded-*</InlineCode> utilities.
                                            The named scale (<InlineCode>radius-xs</InlineCode> …{' '}
                                            <InlineCode>radius-4xl</InlineCode>) maps to Figma's
                                            radius variables.
                                        </>
                                    }>
                                    <div className="grid grid-cols-1 gap-spacing-md md:grid-cols-2 lg:grid-cols-3">
                                        {Object.entries(designTokens.borderRadius).map(
                                            ([name, value]) => (
                                                <RadiusRow key={name} name={name} value={value} />
                                            ),
                                        )}
                                    </div>
                                </Section>

                                <Section
                                    title={`Font size (${Object.keys(designTokens.fontSize).length})`}
                                    description={
                                        <>
                                            Use with <InlineCode>text-*</InlineCode>. Each tuple
                                            entry sets size and lineHeight together so callers don't
                                            have to pair them manually.
                                        </>
                                    }>
                                    <div className="space-y-spacing-md">
                                        {Object.entries(designTokens.fontSize).map(
                                            ([name, value]) => (
                                                <FontSizeRow key={name} name={name} value={value} />
                                            ),
                                        )}
                                    </div>
                                </Section>

                                <Section
                                    title={`Font weight (${Object.keys(designTokens.fontWeight).length})`}
                                    description={
                                        <>
                                            Use with <InlineCode>font-*</InlineCode>.
                                        </>
                                    }>
                                    <div className="grid grid-cols-1 gap-spacing-md md:grid-cols-2">
                                        {Object.entries(designTokens.fontWeight).map(
                                            ([name, value]) => (
                                                <FontWeightRow
                                                    key={name}
                                                    name={name}
                                                    value={value}
                                                />
                                            ),
                                        )}
                                    </div>
                                </Section>

                                <Section
                                    title={`Font family (${Object.keys(designTokens.fontFamily).length})`}
                                    description={
                                        <>
                                            Use with <InlineCode>font-*</InlineCode>. Stacks fall
                                            back left-to-right.
                                        </>
                                    }>
                                    <div className="grid grid-cols-1 gap-spacing-md md:grid-cols-2">
                                        {Object.entries(designTokens.fontFamily).map(
                                            ([name, stack]) => (
                                                <FontFamilyRow
                                                    key={name}
                                                    name={name}
                                                    stack={stack}
                                                />
                                            ),
                                        )}
                                    </div>
                                </Section>

                                <Section
                                    title={`Line height (${Object.keys(designTokens.lineHeight).length})`}
                                    description={
                                        <>
                                            Use with <InlineCode>leading-*</InlineCode>.
                                        </>
                                    }>
                                    <div className="grid grid-cols-1 gap-spacing-md md:grid-cols-2 lg:grid-cols-3">
                                        {Object.entries(designTokens.lineHeight).map(
                                            ([name, value]) => (
                                                <TokenValueRow
                                                    key={name}
                                                    group="lineHeight"
                                                    name={name}
                                                    value={value}
                                                    classPrefix="leading"
                                                />
                                            ),
                                        )}
                                    </div>
                                </Section>

                                <Section
                                    title={`Max width (${Object.keys(designTokens.maxWidth).length})`}
                                    description={
                                        <>
                                            Use with <InlineCode>max-w-*</InlineCode>. The{' '}
                                            <InlineCode>container-*</InlineCode> values are
                                            mobile/desktop layout constraints;{' '}
                                            <InlineCode>width-*</InlineCode> are the named widths
                                            from Foundations.
                                        </>
                                    }>
                                    <div className="grid grid-cols-1 gap-spacing-md md:grid-cols-2 lg:grid-cols-3">
                                        {Object.entries(designTokens.maxWidth).map(
                                            ([name, value]) => (
                                                <TokenValueRow
                                                    key={name}
                                                    group="maxWidth"
                                                    name={name}
                                                    value={value}
                                                    classPrefix="max-w"
                                                />
                                            ),
                                        )}
                                    </div>
                                </Section>
                            </>
                        )}
                    </div>
                </VerificationProvider>
            </Restricted>
        </Page>
    )
}

export default DesignSystemPage
